import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowUpRight, Phone, Search, Star, Clock, MapPin, ShieldCheck, Plane, Headphones, BadgePercent } from 'lucide-react';
import { BACKEND_URL } from '../../config';
import SEO from '../SEO';
import VideoReviews from '../site/VideoReviews';
import { packageImage, packageFallback, onImgError } from '../site/packageImages';
import { isInternational, taxLabel } from '../site/taxes';
import { getDestination } from '../site/destinations';
import './NewHomePage.css';

const PHONE = '+91 96188 49933';
const PHONE_LINK = 'tel:+919618849933';
const WHATSAPP = 'https://wa.me/919716222279?text=' + encodeURIComponent('Hi VK Tours, I would like a holiday quote.');

const img = (id, w = 1200) => `https://images.unsplash.com/${id}?w=${w}&q=80&auto=format&fit=crop`;

const HERO_SLIDES = [
  { place: 'Maldives', image: img('photo-1688949078626-a358f500e063', 2000), path: '/maldives-honeymoon-packages' },
  { place: 'Kashmir', image: img('photo-1595815771614-ade9d652a65d', 2000), path: '/kashmir-tour-packages' },
  { place: 'Dubai', image: img('photo-1512453979798-5ea266f8880c', 2000), path: '/dubai-tour-packages' },
];

// destination key in /api/packages -> label + landing page
const DESTINATIONS = {
  maldives: { name: 'Maldives', path: '/maldives-honeymoon-packages', region: 'intl' },
  dubai: { name: 'Dubai', path: '/dubai-tour-packages', region: 'intl' },
  thailand: { name: 'Thailand', path: '/thailand-tour-packages', region: 'intl' },
  bali: { name: 'Bali', path: '/bali-holiday-packages', region: 'intl' },
  singapore: { name: 'Singapore', path: '/singapore-tour-packages', region: 'intl' },
  vietnam: { name: 'Vietnam', path: '/vietnam-tour-packages', region: 'intl' },
  europe: { name: 'Europe', path: '/europe-tour-packages', region: 'intl' },
  japan: { name: 'Japan', path: '/japan-tour-packages', region: 'intl' },
  srilanka: { name: 'Sri Lanka', path: '/sri-lanka-tour-packages', region: 'intl' },
  malaysia: { name: 'Malaysia', path: '/malaysia-tour-packages', region: 'intl' },
  mauritius: { name: 'Mauritius', path: '/mauritius-honeymoon-packages', region: 'intl' },
  kashmir: { name: 'Kashmir', path: '/kashmir-tour-packages', region: 'india' },
  kerala: { name: 'Kerala', path: '/kerala-tour-packages', region: 'india' },
  rajasthan: { name: 'Rajasthan', path: '/rajasthan-tour-packages', region: 'india' },
  goa: { name: 'Goa', path: '/goa-holiday-packages', region: 'india' },
  andaman: { name: 'Andaman', path: '/andaman-tour-packages', region: 'india' },
  himachal: { name: 'Himachal', path: '/himachal-tour-packages', region: 'india' },
  ladakh: { name: 'Ladakh', path: '/ladakh-bike-tour-packages', region: 'india' },
  northeast: { name: 'North East', path: '/northeast-india-tour-packages', region: 'india' },
  uttarakhand: { name: 'Uttarakhand', path: '/uttarakhand-tour-packages', region: 'india' },
};

const MOSAIC = ['maldives', 'kashmir', 'dubai', 'kerala', 'bali', 'thailand'];

const FALLBACK_IMAGES = {
  maldives: img('photo-1688949078626-a358f500e063'),
  kashmir: img('photo-1595815771614-ade9d652a65d'),
  dubai: img('photo-1512453979798-5ea266f8880c'),
  kerala: img('photo-1602216056096-3b40cc0c9944'),
  bali: img('photo-1711609110590-5ad5c4599e56'),
  thailand: img('photo-1552465011-b4e21bf6e79a'),
};

const STYLES = [
  { name: 'Honeymoon', note: 'Overwater villas, private dinners', path: '/honeymoon-packages', image: img('photo-1615966650071-855b15f29ad1', 900) },
  { name: 'Family', note: 'Kid-friendly hotels & easy pacing', path: '/family-holiday-packages', image: img('photo-1602216056096-3b40cc0c9944', 900) },
  { name: 'Pilgrimage', note: 'Char Dham, Kailash, temple circuits', path: '/pilgrimage-tour-packages', image: img('photo-1600198632203-6ed06fa3ad7c', 900) },
  { name: 'Adventure', note: 'Ladakh bikes, treks, Himalayan roads', path: '/adventure-tour-packages', image: img('photo-1506905925346-21bda4d32df4', 900) },
  { name: 'Group Tours', note: 'Fixed departures with tour manager', path: '/group-tours', image: img('photo-1477587458883-47145ed94245', 900) },
  { name: 'Heritage', note: 'Forts, palaces & old cities', path: '/heritage-tour-packages', image: img('photo-1524492412937-b28074a5d7da', 900) },
];

const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
};
const inr = (n) => '₹' + Math.round(n).toLocaleString('en-IN');

const PackageCard = ({ pkg }) => {
  const price = toNumber(pkg.price);
  const original = toNumber(pkg.originalPrice);
  const off = price && original && original > price ? Math.round(((original - price) / original) * 100) : null;
  const dest = getDestination(pkg.destination);
  return (
    <Link className="vk-card" to={`/destination/${(pkg.destination || '').toLowerCase()}/package/${pkg.id}`} data-testid="home-package-card">
      <div className="vk-card__media">
        <img src={packageImage(pkg)} alt={pkg.title} loading="lazy" onError={onImgError(packageFallback(pkg))} />
                <span className="vk-card__place"><MapPin size={14} /> {dest ? dest.name : pkg.destination}</span>
      </div>
      <div className="vk-card__body">
        <h3 className="vk-card__title">{pkg.title}</h3>
        <div className="vk-card__meta">
          <span><Clock size={14} /> {pkg.duration}</span>
          
        </div>
        <div className="vk-card__price">
          <div>
                        <strong>{price ? inr(price) : 'On request'}</strong>
            <small>per person {taxLabel(isInternational(pkg))}</small>
          </div>
          <span className="vk-card__go" aria-hidden="true"><ArrowUpRight size={20} /></span>
        </div>
      </div>
    </Link>
  );
};

const NewHomePage = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [slide, setSlide] = useState(0);
  const [region, setRegion] = useState('intl');

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/packages`)
      .then((r) => { if (alive) setPackages(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    axios.get(`${BACKEND_URL}/api/hot-deals`)
      .then((r) => { if (alive) setDeals((Array.isArray(r.data) ? r.data : []).filter((d) => d.is_active !== false)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const byDestination = useMemo(() => {
    const map = {};
    packages.forEach((p) => {
      const key = (p.destination || '').toLowerCase();
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return map;
  }, [packages]);

  const minPrices = useMemo(() => {
    const out = {};
    Object.entries(byDestination).forEach(([key, list]) => {
      const prices = list.map((p) => toNumber(p.price)).filter(Boolean);
      out[key] = prices.length ? Math.min(...prices) : null;
    });
    return out;
  }, [byDestination]);
  const fromPrice = (key) => minPrices[key] || null;

  const trending = useMemo(() => {
    const scored = packages
      .filter((p) => toNumber(p.price))
      .map((p) => ({ p, score: (p.bestSeller ? 2 : 0) + (Number(p.rating) || 0) + Math.min(Number(p.reviewCount) || 0, 500) / 500 }))
      .sort((a, b) => b.score - a.score);
    const seen = new Set();
    const out = [];
    for (const { p } of scored) {
      const key = (p.destination || '').toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(p);
      if (out.length === 8) break;
    }
    return out;
  }, [packages]);

  const regionList = useMemo(() => Object.entries(DESTINATIONS)
    .filter(([, d]) => d.region === region)
    .map(([key, d]) => ({ key, ...d, count: (byDestination[key] || []).length, from: minPrices[key] || null }))
    .filter((d) => d.count > 0),
  [region, byDestination, minPrices]);

  const onSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/tour-packages?search=${encodeURIComponent(q)}` : '/tour-packages');
  };

  const current = HERO_SLIDES[slide];

  return (
    <main className="vk-home">
      <SEO
        title="Book Best Holiday Packages | International & Domestic Tours"
        description="Book customised holiday tour packages with VK Tours — Maldives, Dubai, Thailand, Bali, Kashmir, Kerala & 100+ destinations. Best price guarantee, visa assistance, 24×7 support."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'TravelAgency',
          name: 'VK Tours',
          url: 'https://vktours.com',
          telephone: '+91-9618849933',
          email: 'info@vktours.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '1st Floor, Sai Plaza, Sanath Nagar, Above APCOB Bank, Kanuru',
            addressLocality: 'Vijayawada',
            addressRegion: 'Andhra Pradesh',
            postalCode: '520007',
            addressCountry: 'IN',
          },
        }}
      />

      {/* HERO */}
      <section className="vk-hero" aria-label="Featured destination">
        {HERO_SLIDES.map((s, i) => (
          <div key={s.place} className={`vk-hero__bg ${i === slide ? 'is-active' : ''}`} style={{ backgroundImage: `url(${s.image})` }} aria-hidden="true" />
        ))}
        <div className="vk-hero__shade" aria-hidden="true" />
        <div className="vk-wrap vk-hero__inner">
          <p className="vk-eyebrow">15 years · 10,000+ travellers · 4 offices</p>
          <h1 className="vk-display vk-hero__title">
            Pack less.<br /><span>See more.</span>
          </h1>
          <p className="vk-hero__lede">
            Handcrafted holidays across India and 30+ countries — flights, hotels, visas and on-trip support, sorted by one team.
          </p>

          <form className="vk-search" onSubmit={onSearch} role="search">
            <Search size={20} aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Where to? Maldives, Kashmir, Dubai…"
              aria-label="Search destinations"
            />
            <button type="submit" className="vk-btn vk-btn--hot">Find tours</button>
          </form>

          <div className="vk-hero__ctas">
            <a className="vk-btn vk-btn--light" href={WHATSAPP} target="_blank" rel="noopener noreferrer">Get a free quote <ArrowRight size={18} /></a>
            <a className="vk-btn vk-btn--ghost" href={PHONE_LINK}><Phone size={18} /> {PHONE}</a>
          </div>

          <div className="vk-hero__foot">
            <button type="button" className="vk-hero__now" onClick={() => navigate(current.path)}>
              Now showing <strong>{current.place}</strong> <ArrowUpRight size={16} />
            </button>
            <div className="vk-hero__dots" role="tablist" aria-label="Hero slides">
              {HERO_SLIDES.map((s, i) => (
                <button key={s.place} type="button" role="tab" aria-selected={i === slide} aria-label={s.place} className={i === slide ? 'is-active' : ''} onClick={() => setSlide(i)} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="vk-proof" aria-label="Why travellers trust VK Tours">
        <div className="vk-wrap vk-proof__grid">
          <div><strong className="vk-display">15+</strong><span>Years in travel</span></div>
          <div><strong className="vk-display">10,000+</strong><span>Happy travellers</span></div>
          <div><strong className="vk-display">4.8★</strong><span>Google rating</span></div>
          <div><strong className="vk-display">24×7</strong><span>On-trip support</span></div>
        </div>
      </section>

      {/* DEALS TICKER */}
      {false && deals.length > 0 && (
        <section className="vk-ticker" aria-label="Hot deals">
          <div className="vk-ticker__label"><BadgePercent size={18} /> Hot deals</div>
          <div className="vk-ticker__track">
            <div className="vk-ticker__row">
              {[...deals, ...deals].map((d, i) => (
                <button key={`${d.id}-${i}`} type="button" onClick={() => d.link && navigate(d.link)} className="vk-ticker__item" aria-hidden={i >= deals.length} tabIndex={i >= deals.length ? -1 : undefined}>
                  <strong>{d.destination}</strong>
                  <em>{d.discount_text}</em>
                  <span>{d.price_text}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DESTINATION MOSAIC */}
      <section className="vk-section">
        <div className="vk-wrap">
          <header className="vk-head">
            <div>
              <p className="vk-kicker">Where to next</p>
              <h2 className="vk-display vk-h2">Most-booked this season</h2>
            </div>
            <Link className="vk-link" to="/tour-packages">All destinations <ArrowRight size={18} /></Link>
          </header>
          <div className="vk-mosaic">
            {MOSAIC.map((key, i) => {
              const d = DESTINATIONS[key];
              const from = fromPrice(key);
              const count = (byDestination[key] || []).length;
              return (
                <Link key={key} className={`vk-tile vk-tile--${i}`} to={d.path}>
                  <img src={FALLBACK_IMAGES[key]} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  <span className="vk-tile__shade" aria-hidden="true" />
                  <span className="vk-tile__text">
                    <span className="vk-display vk-tile__name">{d.name}</span>
                    <span className="vk-tile__meta">
                      {from ? <>from <strong>{inr(from)}</strong></> : 'Custom quotes'}
                      {count ? <> · {count} tours</> : null}
                    </span>
                  </span>
                  <span className="vk-tile__arrow" aria-hidden="true"><ArrowUpRight size={22} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRENDING PACKAGES */}
      <section className="vk-section vk-section--sand">
        <div className="vk-wrap">
          <header className="vk-head">
            <div>
              <p className="vk-kicker">Ready to book</p>
              <h2 className="vk-display vk-h2">Trending packages</h2>
            </div>
            <Link className="vk-link" to="/tour-packages">View all {packages.length || ''} packages <ArrowRight size={18} /></Link>
          </header>
          {loading ? (
            <div className="vk-grid">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="vk-card vk-card--skeleton" />)}</div>
          ) : (
            <div className="vk-grid">
              {trending.map((p) => <PackageCard key={p.id} pkg={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* VIDEO REVIEWS */}
      <VideoReviews />

      {/* INDIA / INTERNATIONAL */}
      <section className="vk-section vk-section--ink">
        <div className="vk-wrap">
          <header className="vk-head vk-head--light">
            <div>
              <p className="vk-kicker">Pick your map</p>
              <h2 className="vk-display vk-h2">{region === 'intl' ? 'Go international' : 'Incredible India'}</h2>
            </div>
            <div className="vk-toggle" role="tablist" aria-label="Region">
              <button type="button" role="tab" aria-selected={region === 'intl'} className={region === 'intl' ? 'is-active' : ''} onClick={() => setRegion('intl')}>International</button>
              <button type="button" role="tab" aria-selected={region === 'india'} className={region === 'india' ? 'is-active' : ''} onClick={() => setRegion('india')}>India</button>
            </div>
          </header>
          <div className="vk-destlist">
            {regionList.map((d) => (
              <Link key={d.key} className="vk-destlist__item" to={d.path}>
                <span className="vk-display vk-destlist__name">{d.name}</span>
                <span className="vk-destlist__meta">{d.count} tours{d.from ? ` · from ${inr(d.from)}` : ''}</span>
                <ArrowUpRight size={22} className="vk-destlist__arrow" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOLIDAY STYLES */}
      <section className="vk-section">
        <div className="vk-wrap">
          <header className="vk-head">
            <div>
              <p className="vk-kicker">Travel your way</p>
              <h2 className="vk-display vk-h2">Choose your holiday style</h2>
            </div>
          </header>
          <div className="vk-styles">
            {STYLES.map((s) => (
              <Link key={s.name} className="vk-style" to={s.path}>
                <img src={s.image} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                <span className="vk-style__shade" aria-hidden="true" />
                <span className="vk-style__text">
                  <span className="vk-display vk-style__name">{s.name}</span>
                  <span className="vk-style__note">{s.note}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="vk-section vk-section--sand">
        <div className="vk-wrap vk-why">
          <div className="vk-why__intro">
            <p className="vk-kicker">Why VK Tours</p>
            <h2 className="vk-display vk-h2">One team.<br />Door to door.</h2>
            <p>From the first quote to the airport drop back home, you deal with the same people — in Vijayawada, Delhi, the USA and the Maldives.</p>
          </div>
          <ol className="vk-why__list">
            <li><ShieldCheck size={26} /><div><h3>Best price, no surprises</h3><p>Clear quotes with flights shown separately, so you know exactly what you pay for.</p></div></li>
            <li><Plane size={26} /><div><h3>Visas handled</h3><p>Dubai, Schengen, Australia, UK, USA — documents checked and filed by our visa desk.</p></div></li>
            <li><Headphones size={26} /><div><h3>24×7 on-trip support</h3><p>A real person on WhatsApp while you travel, not a ticket number.</p></div></li>
            <li><Star size={26} /><div><h3>Rated 4.8 on Google</h3><p>Read what families, couples and groups say after they get home.</p></div></li>
          </ol>
        </div>
      </section>

      {/* BIG CTA */}
      <section className="vk-cta">
        <div className="vk-wrap vk-cta__inner">
          <h2 className="vk-display vk-cta__title">Tell us your dates.<br /><span>We'll handle the rest.</span></h2>
          <div className="vk-cta__actions">
            <a className="vk-btn vk-btn--dark" href={WHATSAPP} target="_blank" rel="noopener noreferrer">WhatsApp for a quote <ArrowRight size={18} /></a>
            <a className="vk-btn vk-btn--outline" href={PHONE_LINK}><Phone size={18} /> {PHONE}</a>
          </div>
          <p className="vk-cta__offices">INDIA: Vijayawada, Delhi • USA • Maldives</p>
        </div>
      </section>
    </main>
  );
};

export default NewHomePage;
